/**
 * Free Trial Store
 *
 * Manages the free trial system that gives free tier users
 * one drop with Pro features enabled.
 */

// Storage key for localStorage persistence
const TRIAL_STORAGE_KEY = 'photodrop_free_trial'

export interface TrialState {
  // The ID of the trial drop (if created)
  trialDropId: string | null
  // Whether the trial has been used
  trialUsed: boolean
  // When the trial was used
  trialUsedAt?: string
}

// Get trial state from localStorage
export function getTrialState(): TrialState {
  const stored = localStorage.getItem(TRIAL_STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      // Return default state if parse fails
    }
  }
  return {
    trialDropId: null,
    trialUsed: false,
  }
}

// Save trial state to localStorage
export function saveTrialState(state: TrialState): void {
  localStorage.setItem(TRIAL_STORAGE_KEY, JSON.stringify(state))
}

// Check if free trial is available (not yet used)
export function isTrialAvailable(): boolean {
  const state = getTrialState()
  return !state.trialUsed
}

// Use the free trial for a drop
export function useTrialForDrop(dropId: string): boolean {
  const state = getTrialState()

  // Cannot use trial if already used
  if (state.trialUsed) {
    return false
  }

  saveTrialState({
    trialDropId: dropId,
    trialUsed: true,
    trialUsedAt: new Date().toISOString(),
  })

  return true
}

// Check if a specific drop is a trial drop
export function isTrialDrop(dropId: string): boolean {
  const state = getTrialState()
  return state.trialDropId === dropId
}

// Get the trial drop ID (if exists)
export function getTrialDropId(): string | null {
  const state = getTrialState()
  return state.trialDropId
}

// Reset trial (for testing)
export function resetTrial(): void {
  localStorage.removeItem(TRIAL_STORAGE_KEY)
}

// Pro features available in trial:
export const TRIAL_PRO_FEATURES = [
  'Full resolution downloads',
  'Custom branding',
  'Unlimited photos',
  'Priority support',
] as const
